import React, { useState, useEffect } from 'react'
import { Button } from 'components/Core/Button'
import { CloseButton } from 'components/Core/CloseButton'

export interface MonthRange {
    startYear: number
    startMonth: number // 0-indexed
    endYear: number
    endMonth: number  // 0-indexed
}

interface MonthRangePickerProps {
    selected?: MonthRange | null
    onChange?: (range: MonthRange | null) => void
    className?: string
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function monthIndex(year: number, month: number) {
    return year * 12 + month
}

function formatMonthRange(range: MonthRange | null): string {
    if (!range) return 'Select Month Range'
    const { startYear, startMonth, endYear, endMonth } = range
    const count = monthIndex(endYear, endMonth) - monthIndex(startYear, startMonth) + 1
    if (startYear === endYear && startMonth === endMonth) {
        return `${MONTH_NAMES[startMonth]} ${startYear}`
    }
    const sameYear = startYear === endYear
    const startStr = sameYear ? MONTH_NAMES[startMonth] : `${MONTH_NAMES[startMonth]} ${startYear}`
    return `${startStr} – ${MONTH_NAMES[endMonth]} ${endYear} (${count} mo)`
}

export function MonthRangePicker(props: MonthRangePickerProps) {
    const { selected, onChange, className } = props

    const now = new Date()
    const [open, setOpen] = useState(false)
    const [viewYear, setViewYear] = useState(selected ? selected.endYear : now.getFullYear())
    const [hoverMonth, setHoverMonth] = useState<{ year: number; month: number } | null>(null)
    const [picking, setPicking] = useState<{ year: number; month: number } | null>(null)

    function collapse() {
        setOpen(false)
        setPicking(null)
        setHoverMonth(null)
    }

    useEffect(() => {
        window.addEventListener('click', collapse)
        return () => window.removeEventListener('click', collapse)
    }, [])

    function getMonthState(year: number, month: number) {
        const idx = monthIndex(year, month)
        if (selected) {
            const si = monthIndex(selected.startYear, selected.startMonth)
            const ei = monthIndex(selected.endYear, selected.endMonth)
            if (idx === si || idx === ei) return 'selected'
            if (idx > si && idx < ei) return 'in-range'
        }
        if (picking && hoverMonth) {
            const pi = monthIndex(picking.year, picking.month)
            const hi = monthIndex(hoverMonth.year, hoverMonth.month)
            const lo = Math.min(pi, hi)
            const hh = Math.max(pi, hi)
            if (idx === lo || idx === hh) return 'hover-start'
            if (idx > lo && idx < hh) return 'hover-range'
        }
        return 'none'
    }

    function handleMonthClick(year: number, month: number) {
        if (!picking) {
            setPicking({ year, month })
        } else {
            const pi = monthIndex(picking.year, picking.month)
            const ci = monthIndex(year, month)
            const lo = Math.min(pi, ci)
            const hi = Math.max(pi, ci)
            onChange?.({
                startYear: Math.floor(lo / 12),
                startMonth: lo % 12,
                endYear: Math.floor(hi / 12),
                endMonth: hi % 12,
            })
            setPicking(null)
            setHoverMonth(null)
            setOpen(false)
        }
    }

    function handlePreset(months: number) {
        const ei = monthIndex(now.getFullYear(), now.getMonth())
        const si = ei - months + 1
        onChange?.({
            startYear: Math.floor(si / 12), startMonth: si % 12,
            endYear: now.getFullYear(), endMonth: now.getMonth(),
        })
        collapse()
    }

    function handleThisYear() {
        onChange?.({
            startYear: now.getFullYear(), startMonth: 0,
            endYear: now.getFullYear(), endMonth: now.getMonth(),
        })
        collapse()
    }

    function handleLastYear() {
        const y = now.getFullYear() - 1
        onChange?.({ startYear: y, startMonth: 0, endYear: y, endMonth: 11 })
        collapse()
    }

    function handleClear() {
        onChange?.(null)
        collapse()
    }

    // Preview label while picking
    let displayText = formatMonthRange(selected)
    if (picking) {
        if (hoverMonth) {
            const pi = monthIndex(picking.year, picking.month)
            const hi = monthIndex(hoverMonth.year, hoverMonth.month)
            const lo = Math.min(pi, hi)
            const hh = Math.max(pi, hi)
            displayText = formatMonthRange({
                startYear: Math.floor(lo / 12), startMonth: lo % 12,
                endYear: Math.floor(hh / 12), endMonth: hh % 12,
            })
        } else {
            displayText = `${MONTH_NAMES[picking.month]} ${picking.year} → pick end`
        }
    }

    return (
        <div className="date-range month-range-picker" onClick={(e) => e.stopPropagation()}>
            <Button
                icon="fas fa-calendar-alt"
                className={[className, selected ? 'filtered' : ''].filter(Boolean).join(' ')}
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
            >
                {displayText}
            </Button>

            {open && (
                <div className="date-picker-wrapper month-range-wrapper" onClick={(e) => e.stopPropagation()}>
                    <CloseButton className="alt no-bg" onClick={collapse} />

                    {picking && (
                        <div className="month-range-hint">
                            <i className="fas fa-info-circle" />
                            {' '}Now click an end month
                            <button type="button" className="month-range-cancel" onClick={() => { setPicking(null); setHoverMonth(null) }}>
                                × cancel
                            </button>
                        </div>
                    )}

                    <div className="month-range-calendar">
                        <div className="month-range-year-nav">
                            <button type="button" className="mrp-nav-btn" onClick={() => setViewYear(y => y - 1)}>
                                <i className="fas fa-chevron-left" />
                            </button>
                            <span className="month-range-year-label">{viewYear}</span>
                            <button
                                type="button"
                                className="mrp-nav-btn"
                                onClick={() => setViewYear(y => y + 1)}
                                disabled={viewYear >= now.getFullYear()}
                            >
                                <i className="fas fa-chevron-right" />
                            </button>
                        </div>

                        <div className="month-range-grid">
                            {MONTH_NAMES.map((name, mi) => {
                                const isFuture = viewYear === now.getFullYear() && mi > now.getMonth()
                                const state = getMonthState(viewYear, mi)
                                const isEdge = state === 'selected' || state === 'hover-start'
                                const cls = [
                                    'month-cell',
                                    isEdge ? 'month-cell--selected' : '',
                                    state === 'in-range' ? 'month-cell--in-range' : '',
                                    state === 'hover-range' ? 'month-cell--hover-range' : '',
                                    isFuture ? 'month-cell--disabled' : '',
                                ].filter(Boolean).join(' ')
                                return (
                                    <button
                                        key={mi}
                                        type="button"
                                        className={cls}
                                        disabled={isFuture}
                                        onClick={() => handleMonthClick(viewYear, mi)}
                                        onMouseEnter={() => picking && !isFuture && setHoverMonth({ year: viewYear, month: mi })}
                                        onMouseLeave={() => setHoverMonth(null)}
                                    >
                                        {name}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div className="date-ranges">
                        <Button onClick={() => handlePreset(3)}>Last 3 months</Button>
                        <Button onClick={() => handlePreset(6)}>Last 6 months</Button>
                        <Button onClick={() => handlePreset(12)}>Last 12 months</Button>
                        <Button onClick={handleThisYear}>This year</Button>
                        <Button onClick={handleLastYear}>Last year</Button>
                        <Button onClick={handleClear}>Clear</Button>
                    </div>
                </div>
            )}
        </div>
    )
}


